Backbone = require 'backbone'
Action = require 'models/action'
Card = require 'models/card'
Checklist = require 'models/checklist'
Board = require 'models/board'
BoardPlugin = require 'models/board-plugin'
Enterprise = require 'models/enterprise'
EventsMixin = require 'lib/events-mixin'
Label = require 'models/label'
List = require 'models/list'
Member = require 'models/member'
Notification = require 'models/notification'
Organization  = require 'models/organization'
Plugin = require 'models/plugin'
PluginData = require 'models/plugin-data'
Promise = require 'bluebird'
Util = require 'lib/util'
_ = require 'underscore'
log = require 'debug/log'
mixin = require 'lib/mixin'
f = require 'effing'

collections =
  Action:
    type: Action
    test: f.eq('actions')
  Card:
    type: Card
    test: f.eq('cards')
  Checklist:
    type: Checklist
    test: f.eq('checklists')
  Board:
    type: Board
    test: (s) -> /^board($|s)/.test(s)
  BoardPlugin:
    type: BoardPlugin
    test: f.eq('boardPlugins')
  Collaborator:
    type: Member
    test: f.eq('collaborators')
    shouldPreserve: true
  Enterprise:
    type: Enterprise
    test: (s) -> /^enterprise/.test(s)
  Label:
    type: Label
    test: f.eq('labels')
  List:
    type: List
    test: (s) -> /^lists?$/.test(s)
  Member:
    type: Member
    test: (s) -> /^member/.test(s) && s !in ['memberships', 'memberType', 'membershipCounts']
  Notification:
    type: Notification
    test: f.eq('notifications')
  Organization:
    type: Organization
    test: (s) -> /^organization/.test(s)
  Plugin:
    type: Plugin
    test: (s) -> /^plugins?$/.test(s)
  PluginData:
    type: PluginData
    test: f.eq('pluginData')

mixin EventsMixin, class ModelCache
  constructor: ->
    @_cache = {}
    @_locked = []
    @_lockIndex = 0
    @_deltaQueue = []
    @uniqueId = _.uniqueId('mc')

    for typeName of collections
      @_cache[typeName] = {}
    return

  get: (typeOrTypeName, idOrIds) ->
    if _.isArray(idOrIds)
      idOrIds.map f(@, 'get', typeOrTypeName)
    else
      if Util.isShortLink(idOrIds)
        @findOne typeOrTypeName, 'shortLink', idOrIds
      else
        if !_.isString(typeOrTypeName)
          typeOrTypeName = typeOrTypeName::typeName
        @_cache[typeOrTypeName]?[idOrIds]

  find: (modelType, args...) ->
    if args.length == 1
      [predicate] = args
      _.filter @all(modelType), predicate
    else
      [attr, value] = args
      @find modelType, (entry) ->
        entry.get(attr) == value

  findOne: (args...) ->
    _.first @find(args...)

  waitFor: (modelType, id, next) ->
    model = @get(modelType, id)
    if model?
      next(null, model)
    else
      # We can wait for the model to show up
      @once "add:#{ modelType }:#{ id }", =>
        @waitFor modelType, id, next

  all: (typeOrTypeName) ->
    if !_.isString(typeOrTypeName)
      typeOrTypeName = typeOrTypeName::typeName
    _.values(@_cache[typeOrTypeName])

  add: (model) ->
    # Do nothing if the model is already in the model cache
    if model.id && @get(model.typeName, model.id)?
      return

    # Do nothing if we aren't caching this kind of model
    if !collections[model.typeName]?
      return

    hashId = if model.id then model.id else model.cid
    @_cache[model.typeName][hashId] = model

    # I don't know why this is here. It was here before.
    # I can't see how it does anything. I'm scared to take
    # it out. But we should eventually.
    @stopListening model

    @listenTo model, 'change', @onModelChange
    @listenTo model, 'destroy', @remove

    # Send model add events after we've hooked up our own listeners, in case
    # something changes a model in reaction to it being added (e.g. auto
    # marking notifications as read if you're looking at a card)
    @waitForId model, (id) =>
      delete @_cache[model.typeName][model.cid]
      @_cache[model.typeName][id] = model
      @trigger("add:#{ model.typeName }:#{ id }", model)
      model.triggerCacheEvents?(@, 'add', model)

    @trigger("add:#{ model.typeName }", model)
    return

  onModelChange: (model) ->
    model.triggerCacheEvents?(@, 'change', model)
    @trigger("change:#{ model.typeName }", model)
    for attr, value of model.changedAttributes()
      @trigger("change:#{ model.typeName }:#{ attr }", model)
    return

  remove: (model) ->
    return if !model?

    if model instanceof Card
      for action in model.actionList.models
        @remove(action)

    delete @_cache[model.typeName][model.id]
    delete @_cache[model.typeName][model.cid]

    @trigger("remove:#{ model.typeName }", model)
    if model.id?
      @trigger("remove:#{ model.typeName }:#{ model.id }", model)
    model.triggerCacheEvents?(@, 'remove', model)
    @stopListening model
    model.trigger 'destroy'
    model.destructor()

  ###
  Take the response to an API call, and add to (or update) our cache
  The response to an API call might look like this:

  {
    name: "Some board"
    desc: "Some desc"
    cards: [
      { name: "Card 1" }
      { name: "Card 2" }
    ]
    actions: [ ... ]
  }

  model can be either the type of a model (e.g. Board) or an actual model
  (the second case is important if you know that the data is meant to
  update a model that was created, but doesn't have an id yet)
  ###
  _update: (modelOrType, data, mappingRules = {}) ->
    if _.isArray(data)
      return data.map (delta) =>
        @_update(modelOrType, delta, mappingRules)
    else if data.deleted
      @remove @get(modelOrType, data.id)
      return

    setData = _.clone(data)

    mappers = for key, fn of mappingRules when key of setData
      delta = setData[key]
      delete setData[key]
      f(fn, delta)

    for collectionName, { type, test, shouldPreserve } of collections
      for key, value of data when test(key)
        @_update(collectionName, value, mappingRules)
        if !shouldPreserve
          delete setData[key]

    # At this point, we should have only model attributes left; we need to
    # either create a new model, or update an existing one

    existingModel = if _.isString(modelOrType)
      @get(modelOrType, data.id)
    else
      modelOrType

    if existingModel?
      @unmarkModel(existingModel)
      existingModel.set(setData)
      mappers.forEach (fn) -> fn(existingModel)
      return existingModel

    typeInfo = collections[modelOrType]
    if !typeInfo
      return data

    # The TrelloModel constructor adds itself to the cache for us
    newModel = new (typeInfo.type)(setData, modelCache: @)
    mappers.forEach (fn) -> fn(newModel)
    return newModel

  lock: (msg) ->
    lockIndex = @_lockIndex++
    log('MC', "#{ msg } took lock #{ lockIndex }")
    @_locked.push(lockIndex)
    lockIndex

  locked: ->
    !_.isEmpty(@_locked)

  unlock: (lockIndex) ->
    if lockIndex in @_locked
      log('MC', "gave lock #{ lockIndex }")
      @_locked = _.without(@_locked, lockIndex)
      if !@locked()
        # If anything was waiting on the lock, process it now
        @processDeltas()
      true
    else
      false

  getLock: ->
    Promise.resolve @lock()
    .disposer (index) => @unlock(index)

  enqueueDelta: (modelOrType, delta, mappingRules = {}, next = ->) ->
    if _.isFunction(mappingRules)
      next = mappingRules
      mappingRules = {}

    log('MC', "got an update for #{ if _.isString(modelOrType) then modelOrType else modelOrType.typeName }")
    if @locked()
      @_deltaQueue.push { modelOrType, delta, mappingRules, next }
    else
      next null, @_update(modelOrType, delta, mappingRules)
    return

  processDeltas: ->
    queue = @_deltaQueue
    @_deltaQueue = []

    # First, see if we've got any updates that are for models, where the id wasn't
    # set; these always need to go first (to prevent duplicate models being created
    # in cases where we couldn't find the existing model because the id wasn't set
    # yet)
    updateSetsId = (modelOrType) ->
      !_.isString(modelOrType) && !modelOrType.id?

    log('MC', "Running #{ queue.length } updates")

    for { modelOrType, delta, mappingRules, next } in queue when updateSetsId(modelOrType)
      next null, @_update(modelOrType, delta, mappingRules)

    # Now, do the rest of the updates, in the order that they came in
    for { modelOrType, delta, mappingRules, next } in queue when !updateSetsId(modelOrType)
      next null, @_update(modelOrType, delta, mappingRules)

  markModel: (model, marker) ->
    model._marked = marker

  unmarkModel: (model) ->
    delete model._marked

  isMarked: (model, marker) ->
    model._marked == marker

  forEachModel: (fn) ->
    for typeName of @_cache
      for id, model of @_cache[typeName]
        fn(model)
    return

  # Used as part of ModelLoader.replay; mark all the existing models so we
  # know to remove them if we don't get them again as part of reloading all of
  # the data
  mark: ->
    marker = _.uniqueId('mark')

    @forEachModel (model) =>
      @markModel(model, marker)

    marker

  # Remove any models that were marked, and haven't had their marker removed
  sweep: (marker) ->
    @forEachModel (model) =>
      if @isMarked(model, marker)
        @remove(model)
    return


_.extend ModelCache.prototype, Backbone.Events

module.exports = window.ModelCache = new ModelCache()



// WEBPACK FOOTER //
// ./app/scripts/db/model-cache.coffee